import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Check, Loader2, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, formatApiErrorDetail } from "@/context/AuthContext";
import { inrShort } from "@/pages/Dashboard";

export const CATEGORY_LABEL = { eligible: "Eligible ITC", blocked: "Blocked (ineligible)", rcm: "RCM", nil: "Nil / Exempt", zero: "Zero-rated", import_goods: "Import of goods", import_services: "Import of services", non_gst: "Non-GST supply" };
const CATEGORY_CLS = { eligible: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100", blocked: "bg-red-100 text-red-700 hover:bg-red-100", rcm: "bg-sky-100 text-sky-700 hover:bg-sky-100",
  nil: "bg-slate-100 text-slate-600 hover:bg-slate-100", zero: "bg-violet-100 text-violet-700 hover:bg-violet-100",
  import_goods: "bg-amber-100 text-amber-800 hover:bg-amber-100", import_services: "bg-orange-100 text-orange-800 hover:bg-orange-100", non_gst: "bg-stone-100 text-stone-700 hover:bg-stone-100" };

export const CategoryChip = ({ cat, flag }) => cat ? (
  <span className="inline-flex items-center gap-1">
    <Badge className={`${CATEGORY_CLS[cat] || ""} text-[10px]`}>{CATEGORY_LABEL[cat] || cat}</Badge>
    {flag ? <span title={flag} data-testid="gstr2-tax-flag"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span> : null}
  </span>
) : null;

function Row({ c, canEdit, onSaved, onDrill }) {
  const [label, setLabel] = useState(c.label || c.suggested_label);
  const [cat, setCat] = useState(c.category || c.suggested_category);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLabel(c.label || c.suggested_label); setCat(c.category || c.suggested_category); }, [c]);
  const dirty = label !== (c.label || "") || cat !== (c.category || "");
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/reports/gstr2/tax-codes/${encodeURIComponent(c.code)}`, { label, category: cat, rate: c.observed_rate }); toast.success(`Tax code ${c.code} saved`); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/reports/gstr2/tax-codes/${encodeURIComponent(c.code)}`); toast.success(`Tax code ${c.code} reset`); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset"); } finally { setBusy(false); }
  };
  const mismatch = c.mapped && c.observed_itc && CATEGORY_LABEL[c.category] && ((c.category === "blocked" && c.observed_itc !== "No") || (c.category === "eligible" && c.observed_itc !== "Yes") || (c.category === "rcm" && c.observed_itc !== "RCM"));
  return (
    <TableRow className="border-b border-slate-100" data-testid={`gstr2-taxcode-row-${c.code}`}>
      <TableCell className="whitespace-nowrap">
        <button type="button" onClick={() => onDrill({ metric: "tax_code", value: c.code })} className="font-mono text-xs font-semibold text-sky-700 hover:underline" data-testid={`gstr2-taxcode-drill-${c.code}`} title="Lines with this tax code">{c.code}</button>
        {c.mapped ? <Check className="inline h-3 w-3 ml-1 text-emerald-600" /> : null}
        {c.igst_only && !String(c.category).startsWith("import") ? <span className="ml-1.5 rounded bg-amber-50 border border-amber-200 px-1 text-[9px] text-amber-700" title="Only IGST posted on this code — tag it Import of goods / services if it is customs IGST" data-testid={`gstr2-taxcode-igst-only-${c.code}`}>IGST only · import?</span> : null}
      </TableCell>
      <TableCell className="text-xs text-slate-600 whitespace-nowrap">{c.observed_rate == null ? "—" : `${c.observed_rate}%`} · {c.observed_itc || "—"}{mismatch ? <span title="Your category differs from how SAP posts this code" className="ml-1 inline-flex align-middle"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span> : null}</TableCell>
      <TableCell className="text-xs text-right text-slate-600 tabular-nums">{c.count.toLocaleString("en-IN")}</TableCell>
      <TableCell className="text-xs text-right tabular-nums font-semibold">{inrShort(c.total_tax)}</TableCell>
      <TableCell className="min-w-[220px]">{canEdit ? <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 text-xs" data-testid={`gstr2-taxcode-label-${c.code}`} /> : <span className="text-xs text-slate-800">{c.label || <span className="text-slate-400 italic">{c.suggested_label}</span>}</span>}</TableCell>
      <TableCell>{canEdit ? (
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid={`gstr2-taxcode-category-${c.code}`}>
          {Object.entries(CATEGORY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      ) : <CategoryChip cat={c.category || c.suggested_category} />}</TableCell>
      {canEdit && (
        <TableCell className="whitespace-nowrap">
          <Button size="sm" variant={dirty ? "default" : "outline"} className="h-7 text-xs" disabled={busy || (!dirty && c.mapped)} onClick={save} data-testid={`gstr2-taxcode-save-${c.code}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : c.mapped && !dirty ? "Saved" : "Save"}</Button>
          {c.mapped && <Button size="sm" variant="ghost" className="h-7 text-xs ml-1" disabled={busy} onClick={reset} title="Remove mapping" data-testid={`gstr2-taxcode-reset-${c.code}`}><RotateCcw className="h-3 w-3" /></Button>}
        </TableCell>
      )}
    </TableRow>
  );
}

/** Tax-code map: label + category per SAP tax code (admin edits; everyone sees labels on the register). */
export function GSTR2TaxCodes({ canEdit, onDrill, onChanged }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => axios.get(`${API}/reports/gstr2/tax-codes`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const saved = () => { load(); onChanged?.(); };
  return (
    <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="gstr2-taxcodes">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Tax code map</p>
            <p className="text-[11px] text-slate-500">{d ? `${d.codes.length} tax codes in the register · ${d.mapped} named` : "Loading…"} · labels appear on every register line and drill-down; ITC status itself stays GL-driven</p>
          </div>
          <Button variant="outline" size="sm" className="border-slate-300 h-8 text-xs" onClick={() => setOpen((o) => !o)} data-testid="gstr2-taxcodes-toggle">{open ? "Hide" : canEdit ? "Edit tax codes" : "Show tax codes"}</Button>
        </div>
        {open && d && (
          <div className="mt-3 overflow-x-auto max-h-[460px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Code", "SAP shows", "Lines", "Tax", "Your label", "Category", canEdit ? "" : null].filter((h) => h !== null).map((h, i) => <TableHead key={`${h}-${i}`} className={`text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap ${["Lines", "Tax"].includes(h) ? "text-right" : ""}`}>{h}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>{d.codes.map((c) => <Row key={c.code} c={c} canEdit={canEdit} onSaved={saved} onDrill={onDrill} />)}</TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
